/**
 * Shared city graph types — used by both map.ts and playback-map.ts.
 * Source: city_graph.json generated by corpllm-server/engine/cmd/mapgen.
 */

export interface CityNode {
  node_id: string;
  node_type: 'STREET' | 'ALLEY' | 'AVENUE' | 'BUILDING_ENTRY' | 'BUILDING_FOOTPRINT' | 'COURTYARD' | 'BORDER';
  global_x: number;
  global_y: number;
  is_hq?: boolean;
  front_building_id?: string;
  facing?: 'N' | 'E' | 'S' | 'W';
  footprint_of?: string;
}

export interface CityEdge {
  from: string;
  to: string;
  edge_type: 'internal' | 'cross_sector';
}

export interface Sector {
  sector_id: string;
  sector_type: string;
  name: string;
  wealth: number;
  fear: number;
  addiction: number;
  heat: number;
  property_value: number;
  laundering_cap: number;
  grid_row: number;
  grid_col: number;
  adjacent_sectors: string[];
  nodes: CityNode[];
  edges: CityEdge[];
}

export interface CityGraph {
  meta: {
    mode: string;
    grid_rows: number;
    grid_cols: number;
    version?: string;
    engine_version?: string;
    generated?: string;
    ai_corps: { corps: { name: string; sectors: string[] }[] };
  };
  sectors: Sector[];
}

// ── Canvas / camera constants ────────────────────────────────────────────

export const CW = 800;
export const CH = 800;
export const TILE_SIZE = 64;
export const GRID_SIZE = 120;

// World dimensions — default assumes square sectors.
// Call initWorldDimensions() with graph metadata to correct for non-square grids.
export let WORLD_W = GRID_SIZE * TILE_SIZE; // 7680 default
export let WORLD_H = GRID_SIZE * TILE_SIZE; // adjusted at runtime
export let INIT_ZOOM = CW / WORLD_W;
/** Size of one grid cell in world pixels (after initWorldDimensions). */
export let CELL_SIZE = 80;

/** Recalculate world dimensions so sectors (and cells) are square.
 *  Must be called once after city_graph.json is loaded. */
export function initWorldDimensions(gridCols: number, gridRows: number): void {
  const sectorSize = (GRID_SIZE * TILE_SIZE) / gridCols;
  WORLD_W = gridCols * sectorSize;
  WORLD_H = gridRows * sectorSize;
  INIT_ZOOM = CW / Math.max(WORLD_W, WORLD_H);
  CELL_SIZE = sectorSize / 16; // SectorGridRes + 1 = 16
}

// ── Coordinate helpers ───────────────────────────────────────────────────

/** Normalized global_x (0..1) → world pixel coordinate. */
export function toWorldX(gx: number): number { return gx * WORLD_W; }
export function toWorldY(gy: number): number { return gy * WORLD_H; }

// ── Sector background colors ─────────────────────────────────────────────

export const TYPE_BG: Record<string, string> = {
  RESIDENTIAL_LOW:  '#1a2438', RESIDENTIAL_HIGH: '#1a1f38',
  COMMERCIAL:       '#1f1a2e', INDUSTRIAL:       '#1a2420',
  WATERFRONT:       '#111e2a', GOVERNMENT:       '#2a1a1a',
  ENTERTAINMENT:    '#2a1a2e',
};
